/**
 * pages/admin/AdminUsuarios.js
 * Panel de administración de usuarios administradores
 * Solo accesible para AdminLevel = 3
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Badge, Spinner, Alert, InputGroup, Pagination } from 'react-bootstrap';
import { FaPlus, FaEdit, FaKey, FaTrash, FaUndo, FaUsers, FaShieldAlt, FaEye, FaEyeSlash } from 'react-icons/fa';
import NavbarG from '../../modules/administrador/components/NavbarG';
import { toast } from 'react-toastify';
import './AdminUsuarios.css';

const API_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4004' 
  : '';

const ITEMS_POR_PAGINA = 15;

// Niveles de administrador
const ADMIN_LEVELS = [
  { value: 1, label: 'Nivel 1 - Básico', color: 'secondary' },
  { value: 2, label: 'Nivel 2 - Avanzado', color: 'info' },
  { value: 3, label: 'Nivel 3 - Super Admin', color: 'danger' }
];

// Roles
const ROLES = [
  { value: 1, label: 'Administración' },
  { value: 2, label: 'Inspección' },
  { value: 3, label: 'Taller' },
  { value: 4, label: 'Conducción' }
];

const AdminUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usuario, setUsuario] = useState(null);
  
  // Estados del formulario
  const [showFormModal, setShowFormModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [usuarioActual, setUsuarioActual] = useState(null);
  const [formData, setFormData] = useState({
    NmUsuario: '',
    Nombre: '',
    Password: '',
    AdminLevel: 1,
    PortalRRHH: 0
  });

  // Modal de cambio de contraseña
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUsuario, setPasswordUsuario] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Filtros
  const [filtroEstado, setFiltroEstado] = useState('todos'); // activos, inactivos, todos
  const [busqueda, setBusqueda] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setUsuario(user);
      
      // Verificar que tenga AdminLevel = 3
      if (user.AdminLevel !== 3) {
        toast.error('No tienes permisos para acceder a este módulo');
        window.location.href = '/inicio';
        return;
      }
    }
    fetchUsuarios();
  }, []);

  const fetchUsuarios = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/admin/usuarios`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          toast.error('No tienes permisos para acceder a este módulo');
          window.location.href = '/inicio';
          return;
        }
        throw new Error('Error al cargar usuarios');
      }

      const data = await response.json();
      setUsuarios(data.data || []);
    } catch (err) {
      toast.error('Error al cargar usuarios');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // CREAR/EDITAR Usuario
  const handleShowForm = (usuario = null) => {
    if (usuario) {
      setModoEdicion(true);
      setUsuarioActual(usuario);
      setFormData({
        NmUsuario: usuario.NmUsuario,
        Nombre: usuario.Nombre || '',
        Password: '', // No prellenar contraseña
        AdminLevel: usuario.AdminLevel || 1,
        PortalRRHH: usuario.PortalRRHH || 0
      });
    } else {
      setModoEdicion(false);
      setUsuarioActual(null);
      setFormData({
        NmUsuario: '',
        Nombre: '',
        Password: '',
        AdminLevel: 1,
        PortalRRHH: 0
      });
    }
    setShowFormModal(true);
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      if (modoEdicion) {
        // Actualizar usuario (sin password)
        const { Password, NmUsuario, ...updateData } = formData;
        
        // Agregar valores por defecto para campos opcionales
        const dataToSend = {
          ...updateData,
          CdGrupo: updateData.CdGrupo || '1',
          IdRol: updateData.IdRol || 1
        };
        
        const response = await fetch(`${API_URL}/api/admin/usuarios/${usuarioActual.CdUsuario}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(dataToSend)
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Error al actualizar');
        }

        toast.success('Usuario actualizado exitosamente');
      } else {
        // Crear nuevo usuario
        if (!formData.Password || formData.Password.length < 4) {
          toast.error('La contraseña debe tener al menos 4 caracteres');
          return;
        }

        // Agregar valores por defecto para campos opcionales
        const dataToSend = {
          ...formData,
          CdGrupo: formData.CdGrupo || '1',
          IdRol: formData.IdRol || 1
        };

        const response = await fetch(`${API_URL}/api/admin/usuarios`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(dataToSend)
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Error al crear');
        }

        toast.success('Usuario creado exitosamente');
      }

      setShowFormModal(false);
      fetchUsuarios();
    } catch (err) {
      toast.error(err.message || 'Error al guardar usuario');
      console.error(err);
    }
  };

  // CAMBIAR CONTRASEÑA
  const handleShowPasswordModal = (usuario) => {
    setPasswordUsuario(usuario);
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowPasswordModal(true);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (newPassword.length < 4) {
      toast.error('La contraseña debe tener al menos 4 caracteres');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/admin/usuarios/${passwordUsuario.CdUsuario}/password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newPassword })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error al cambiar contraseña');
      }

      toast.success('Contraseña actualizada exitosamente');
      setShowPasswordModal(false);
    } catch (err) {
      toast.error(err.message || 'Error al cambiar contraseña');
      console.error(err);
    }
  };

  // TOGGLE PORTAL RRHH
  const handleTogglePortalRRHH = async (usuario) => {
    const nuevoEstado = usuario.PortalRRHH === 1 ? 0 : 1;
    const accion = nuevoEstado === 1 ? 'activar' : 'desactivar';
    
    if (!window.confirm(`¿Estás seguro de ${accion} el acceso a Portal RRHH para "${usuario.Nombre}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/admin/usuarios/${usuario.CdUsuario}/portal-rrhh`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ PortalRRHH: nuevoEstado })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Error al ${accion} Portal RRHH`);
      }

      toast.success(`Portal RRHH ${nuevoEstado === 1 ? 'activado' : 'desactivado'} exitosamente`);
      fetchUsuarios();
    } catch (err) {
      toast.error(err.message || 'Error al cambiar estado de Portal RRHH');
      console.error(err);
    }
  };

  // Filtrar usuarios
  const usuariosFiltrados = usuarios.filter(user => {
    // Filtro por estado (IMPORTANTE: verificar null/undefined)
    const esInactivo = user.Baja === 1 || user.FecBaja != null;
    if (filtroEstado === 'activos' && esInactivo) return false;
    if (filtroEstado === 'inactivos' && !esInactivo) return false;
    
    // Filtro por búsqueda
    if (busqueda) {
      const searchLower = busqueda.toLowerCase();
      return (
        (user.Nombre && user.Nombre.toLowerCase().includes(searchLower)) ||
        (user.NmUsuario && user.NmUsuario.toLowerCase().includes(searchLower))
      );
    }
    
    return true;
  });

  const getBadgeColor = (adminLevel) => {
    const level = ADMIN_LEVELS.find(l => l.value === adminLevel);
    return level ? level.color : 'secondary';
  };

  return (
    <div className="admin-usuarios-page">
      <NavbarG />
      
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <div className="page-header">
              <div className="d-flex align-items-center gap-3">
                <div className="icon-wrapper">
                  <FaShieldAlt size={32} />
                </div>
                <div>
                  <h2 className="mb-0">Gestión de Usuarios Administradores</h2>
                  <p className="text-muted mb-0">Control total de usuarios del sistema</p>
                </div>
              </div>
              <Button variant="primary" onClick={() => handleShowForm()}>
                <FaPlus className="me-2" />
                Nuevo Usuario
              </Button>
            </div>
          </Col>
        </Row>

        {/* Filtros y búsqueda */}
        <Row className="mb-3">
          <Col md={6}>
            <InputGroup>
              <InputGroup.Text>
                <FaUsers />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Buscar por nombre o usuario..."
                value={busqueda}
                onChange={(e) => {
                  setBusqueda(e.target.value);
                  setPaginaActual(1);
                }}
              />
            </InputGroup>
          </Col>
          <Col md={6}>
            <Form.Select 
              value={filtroEstado} 
              onChange={(e) => {
                setFiltroEstado(e.target.value);
                setPaginaActual(1);
              }}
            >
              <option value="activos">Usuarios Activos</option>
              <option value="inactivos">Usuarios Inactivos</option>
              <option value="todos">Todos los Usuarios</option>
            </Form.Select>
          </Col>
        </Row>

        {/* Tabla de usuarios */}
        <Row>
          <Col>
            <Card className="shadow-sm">
              <Card.Body>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Cargando usuarios...</p>
                  </div>
                ) : usuariosFiltrados.length === 0 ? (
                  <Alert variant="info" className="text-center">
                    <FaUsers size={40} className="mb-3" />
                    <p className="mb-0">No se encontraron usuarios con los filtros aplicados</p>
                  </Alert>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table hover className="usuarios-table">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Usuario</th>
                            <th>Nombre Completo</th>
                            <th>Nivel Admin</th>
                            <th>Portal RRHH</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const totalPaginas = Math.ceil(usuariosFiltrados.length / ITEMS_POR_PAGINA);
                            const inicio = (paginaActual - 1) * ITEMS_POR_PAGINA;
                            const usuariosPaginados = usuariosFiltrados.slice(inicio, inicio + ITEMS_POR_PAGINA);
                            
                            return usuariosPaginados.map(user => (
                          <tr key={user.CdUsuario} className={user.Baja === 1 ? 'usuario-inactivo' : ''}>
                            <td className="font-monospace">{user.CdUsuario}</td>
                            <td>
                              <strong>{user.NmUsuario}</strong>
                            </td>
                            <td>{user.Nombre || '-'}</td>
                            <td>
                              <Badge bg={getBadgeColor(user.AdminLevel)}>
                                Nivel {user.AdminLevel}
                              </Badge>
                            </td>
                            <td className="text-center">
                              {user.PortalRRHH === 1 ? (
                                <Badge bg="success">Sí</Badge>
                              ) : (
                                <Badge bg="secondary">No</Badge>
                              )}
                            </td>
                            <td>
                              {user.Baja === 1 ? (
                                <Badge bg="danger">Inactivo</Badge>
                              ) : (
                                <Badge bg="success">Activo</Badge>
                              )}
                            </td>
                            <td>
                              <div className="action-buttons-container">
                                <Button 
                                  variant="outline-info" 
                                  size="sm"
                                  className="action-btn"
                                  onClick={() => handleShowForm(user)}
                                  title="Editar datos del usuario"
                                >
                                  <FaEdit /> <span className="btn-text">Editar</span>
                                </Button>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  className="action-btn"
                                  onClick={() => handleShowPasswordModal(user)}
                                  title="Cambiar contraseña"
                                >
                                  <FaKey /> <span className="btn-text">Clave</span>
                                </Button>
                                <Button 
                                  variant={user.PortalRRHH === 1 ? "success" : "outline-secondary"}
                                  size="sm"
                                  className="action-btn"
                                  onClick={() => handleTogglePortalRRHH(user)}
                                  title={user.PortalRRHH === 1 ? "Desactivar Portal RRHH" : "Activar Portal RRHH"}
                                >
                                  {user.PortalRRHH === 1 ? <FaEye /> : <FaEyeSlash />} <span className="btn-text">{user.PortalRRHH === 1 ? 'Portal' : 'Portal'}</span>
                                </Button>
                              </div>
                            </td>
                            </tr>
                            ));
                          })()}
                        </tbody>
                      </Table>
                    </div>
                    
                    {/* Paginación */}
                    {(() => {
                      const totalPaginas = Math.ceil(usuariosFiltrados.length / ITEMS_POR_PAGINA);
                      const inicio = (paginaActual - 1) * ITEMS_POR_PAGINA;
                      
                      return totalPaginas > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                          <span className="text-muted small">
                            Mostrando {inicio + 1} - {Math.min(inicio + ITEMS_POR_PAGINA, usuariosFiltrados.length)} de {usuariosFiltrados.length}
                          </span>
                          <Pagination className="mb-0">
                            <Pagination.First 
                              onClick={() => setPaginaActual(1)} 
                              disabled={paginaActual === 1} 
                            />
                            <Pagination.Prev 
                              onClick={() => setPaginaActual(p => Math.max(1, p - 1))} 
                              disabled={paginaActual === 1} 
                            />
                            
                            {[...Array(Math.min(5, totalPaginas))].map((_, i) => {
                              let pageNum;
                              if (totalPaginas <= 5) {
                                pageNum = i + 1;
                              } else if (paginaActual <= 3) {
                                pageNum = i + 1;
                              } else if (paginaActual >= totalPaginas - 2) {
                                pageNum = totalPaginas - 4 + i;
                              } else {
                                pageNum = paginaActual - 2 + i;
                              }
                              
                              return (
                                <Pagination.Item
                                  key={pageNum}
                                  active={pageNum === paginaActual}
                                  onClick={() => setPaginaActual(pageNum)}
                                >
                                  {pageNum}
                                </Pagination.Item>
                              );
                            })}
                            
                            <Pagination.Next 
                              onClick={() => setPaginaActual(p => Math.min(totalPaginas, p + 1))} 
                              disabled={paginaActual === totalPaginas} 
                            />
                            <Pagination.Last 
                              onClick={() => setPaginaActual(totalPaginas)} 
                              disabled={paginaActual === totalPaginas} 
                            />
                          </Pagination>
                        </div>
                      );
                    })()}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* MODAL CREAR/EDITAR USUARIO */}
      <Modal show={showFormModal} onHide={() => setShowFormModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modoEdicion ? 'Editar Usuario' : 'Nuevo Usuario'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitForm}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre de Usuario *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="usuario123"
                    maxLength={20}
                    value={formData.NmUsuario}
                    onChange={(e) => setFormData({...formData, NmUsuario: e.target.value})}
                    required
                    disabled={modoEdicion}
                  />
                  <Form.Text className="text-muted">
                    Usuario para login. Máximo 20 caracteres.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre Completo</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Juan Pérez"
                    maxLength={100}
                    value={formData.Nombre}
                    onChange={(e) => setFormData({...formData, Nombre: e.target.value})}
                  />
                  <Form.Text className="text-muted">
                    Opcional.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {!modoEdicion && (
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Contraseña *</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Mínimo 4 caracteres"
                      value={formData.Password}
                      onChange={(e) => setFormData({...formData, Password: e.target.value})}
                      required={!modoEdicion}
                      minLength={4}
                    />
                    <Form.Text className="text-muted">
                      Para cambiar contraseña de usuario existente, usa el botón "Cambiar Contraseña".
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nivel de Administrador *</Form.Label>
                  <Form.Select
                    value={formData.AdminLevel}
                    onChange={(e) => setFormData({...formData, AdminLevel: parseInt(e.target.value)})}
                  >
                    {ADMIN_LEVELS.map(level => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Nivel 3 = Super Admin (acceso completo).
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Acceso Portal RRHH *</Form.Label>
                  <div className="mt-2">
                    <Form.Check
                      type="switch"
                      id="portal-switch"
                      label={formData.PortalRRHH === 1 ? "Permitir Acceso" : "Denegar Acceso"}
                      checked={formData.PortalRRHH === 1}
                      onChange={(e) => setFormData({...formData, PortalRRHH: e.target.checked ? 1 : 0})}
                    />
                  </div>
                  <Form.Text className="text-muted">
                    Permite login en el portal de empleados.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowFormModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              {modoEdicion ? 'Actualizar' : 'Crear Usuario'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODAL CAMBIAR CONTRASEÑA */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Cambiar Contraseña</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleChangePassword}>
          <Modal.Body>
            <Alert variant="info">
              <strong>Usuario:</strong> {passwordUsuario?.Nombre} ({passwordUsuario?.NmUsuario})
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label>Nueva Contraseña *</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Mínimo 4 caracteres"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={4}
                />
                <Button 
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirmar Contraseña *</Form.Label>
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                placeholder="Repetir contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={4}
              />
            </Form.Group>

            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <Alert variant="warning">
                Las contraseñas no coinciden
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={!newPassword || !confirmPassword || newPassword !== confirmPassword}
            >
              Cambiar Contraseña
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminUsuarios;
